import { isReference, resolveId } from '@medplum/core'
import type {
  Bundle,
  ExtractResource,
  Reference,
  ResourceType,
} from '@medplum/fhirtypes'
import type { UseQueryResult } from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import type { UseFhirReadOptions } from '#src/hooks/useFhirRead.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

/**
 * Queries for the history (list of versions) of a FHIR resource.
 * The resource is identified by type and id, and the result is a Bundle of
 * versions of the resource. They are ordered from newest to oldest versions.
 *
 * @param type - resource type
 * @param id - resource id or reference
 * @param options - react-query options
 * @returns query result for a Bundle of versions of the resource
 */
export function useFhirReadHistory<T extends ResourceType>(
  type: T | undefined,
  id?: Reference<ExtractResource<T>> | string | null,
  options?: UseFhirReadOptions<T>
): UseQueryResult<Bundle<ExtractResource<T>>> {
  const medplum = useMedplum()
  const resolvedId = (isReference(id) ? resolveId(id) : (id as string)) ?? ''

  const result = useQuery<Bundle<ExtractResource<T>>>({
    ...(options?.query as any),
    enabled:
      Boolean(type) &&
      Boolean(resolvedId) &&
      (options?.query?.enabled === undefined || options?.query?.enabled),
    queryKey: cacheKeys.readHistory(type, resolvedId),
    queryFn: () => {
      if (!type) {
        throw new Error('Resource type is undefined')
      }
      return medplum.readHistory(type, resolvedId)
    },
  })

  useErrorToast(
    result.error &&
      `Error accessing history for ${type}/${resolvedId}: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}
